import { AnalysisExportSseEventPayload } from '@api-types/sse/event-payloads/analysis-export.sse-event-payload';
import { AnalysisProgressSseEventPayload } from '@api-types/sse/event-payloads/analysis-progress.sse-event-payload';
import { ConsensusCycleInitializationSseEventPayload } from '@api-types/sse/event-payloads/consensus-cycle-initialization.sse-event-payload';
import { ConsensusExportSseEventPayload } from '@api-types/sse/event-payloads/consensus-export.sse-event-payload';
import { ConsensusReconciliationSseEventPayload } from '@api-types/sse/event-payloads/consensus-reconciliation.sse-event-payload';
import { ConsensusTemplateSseEventPayload } from '@api-types/sse/event-payloads/consensus-template.sse-event-payload';
import { ExportSseEventPayload } from '@api-types/sse/event-payloads/export.sse-event-payload';
import { ForecastProgressSseEventPayload } from '@api-types/sse/event-payloads/forecast-progress.sse-event-payload';
import { NotificationSseEventPayload } from '@api-types/sse/event-payloads/notification.sse-event-payload';
import { ReportingImportSseEventPayload } from '@api-types/sse/event-payloads/reporting-import.sse-event-payload';
import { z } from 'zod';

// event type constants
export const SseEventType = {
  ForecastProgress: 'FORECAST_PROGRESS',
  AnalysisProgress: 'ANALYSIS_PROGRESS',
  AnalysisExport: 'ANALYSIS_EXPORT',
  ConsensusExport: 'CONSENSUS_EXPORT',
  ReportingImport: 'REPORTING_IMPORT',
  Heartbeat: 'HEARTBEAT',
  Notification: 'NOTIFICATION',
  ConsensusReconciliation: 'CONSENSUS_RECONCILIATION',
  ConsensusCycleInitialization: 'CONSENSUS_CYCLE_INITIALIZATION',
  ConsensusTemplate: 'CONSENSUS_TEMPLATE',
  Export: 'EXPORT',
} as const;

export type SseEventType = (typeof SseEventType)[keyof typeof SseEventType];

// maps event types to their payload types
export interface SseEventPayloadMap {
  [SseEventType.ForecastProgress]: ForecastProgressSseEventPayload;
  [SseEventType.AnalysisProgress]: AnalysisProgressSseEventPayload;
  [SseEventType.AnalysisExport]: AnalysisExportSseEventPayload;
  [SseEventType.ConsensusExport]: ConsensusExportSseEventPayload;
  [SseEventType.ReportingImport]: ReportingImportSseEventPayload;
  [SseEventType.Heartbeat]: Record<string, never>;
  [SseEventType.Notification]: NotificationSseEventPayload;
  [SseEventType.ConsensusReconciliation]: ConsensusReconciliationSseEventPayload;
  [SseEventType.ConsensusCycleInitialization]: ConsensusCycleInitializationSseEventPayload;
  [SseEventType.ConsensusTemplate]: ConsensusTemplateSseEventPayload;
  [SseEventType.Export]: ExportSseEventPayload;
}

// event envelope sent over the wire
export type SseEvent<T extends SseEventType = SseEventType> = {
  type: T;
  payload: SseEventPayloadMap[T];
  timestamp: number;
};

// validates structure only, payload-type relationship is enforced by typescript at compile time
const SseEventSchema = z.object({
  type: z.nativeEnum(SseEventType),
  payload: z.record(z.string(), z.unknown()),
  timestamp: z.number(),
});

export const isSseEvent = (value: unknown): value is SseEvent => {
  return SseEventSchema.safeParse(value).success;
};
